import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useNavigation, useRoute } from "@react-navigation/core";
import "dayjs/locale/fr";
import * as Clipboard from "expo-clipboard";
import * as Location from "expo-location";
import React, { memo, useEffect, useRef, useState } from "react";
import {
	ActivityIndicator,
	Image,
	ImageBackground,
	SafeAreaView,
	StyleSheet,
	Text,
	TouchableOpacity,
	View
} from "react-native";
import { Avatar, Tile } from "react-native-elements";
import {
	Actions,
	Bubble,
	Composer,
	Day,
	GiftedChat,
	InputToolbar,
	LoadEarlier,
	MessageText,
	Send,
	Time,
	utils
} from "react-native-gifted-chat";
import MapView from "react-native-maps";
import MapViewMarker from "react-native-web-maps/src/Marker";
import { getYoutubeMeta } from "react-native-youtube-iframe";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { v4 } from "uuid";
import { fetchExitChat, fetchGetChatMessages } from "../actions/chat";
import {
	CHAT,
	CLEAR_CHAT_MAP_DATA,
	CLEAR_CHAT_PHOTO_DATA,
	CLEAR_CHAT_VIDEO_ID,
	CLEAR_READY_TO_COPY_URL,
	SET_CHAT_CURRENT_LOCATION,
	SET_CHAT_PHOTO_DATA,
	SET_CHAT_THUMBNAIL_URL,
	SET_CHAT_VIDEO_ID,
	SET_DROP_DOWN_ALERT_INFO,
	SET_IN_CHAT_WITH,
	SET_READY_TO_COPY_URL
} from "../actions/types";
import { getImage, getImageUri } from "../assets/Images";
import { ROLE_NEIGHBOR } from "../config/constants";
import { getChatSelector } from "../selectors";
import { getTranslatedProperty, t } from "../services/i18n";
import commonStyles, {
	COLOR2,
	COLOR5,
	SCREEN_HEIGHT,
	isAndroid,
	isDesktop,
	isWeb,
	isiOS
} from "../styles/commonStyles";
import { pickImage } from "../utils/PickImage";
import { pickDocument } from "../utils/PickDocument";
import { openBrowser, openURL } from "../utils/UrlUtil";
import YoutubeView from "./YoutubeView";
import { getAvatarSource } from "../utils/AvatarUtil";
import { useHeaderHeight } from "@react-navigation/elements";

const { isSameUser, isSameDay } = utils;

// Helper function to check if a URI is a PDF
const isPdfUri = uri => {
	if (!uri) return false;
	const uriLower = uri.toLowerCase();
	return (
		uriLower.endsWith(".pdf") ||
		uriLower.includes("application/pdf") ||
		uriLower.startsWith("data:application/pdf")
	);
};

// Helper function to extract PDF name from URI
const extractPdfNameFromUri = uri => {
	if (!uri) return null;

	// If it's a data URI, try to extract from it
	if (uri.startsWith("data:application/pdf")) {
		// Could contain filename in the data URI, but usually doesn't
		// Check if there's a filename parameter
		const match = uri.match(/filename=([^;]+)/i);
		if (match) {
			try {
				return decodeURIComponent(match[1]).replace(/\.pdf$/i, "");
			} catch {
				return match[1].replace(/\.pdf$/i, "");
			}
		}
		return null;
	}

	// Extract filename from URI path
	try {
		// Handle both regular URLs and file:// URIs
		let path = uri;
		if (uri.includes("://")) {
			// Remove protocol and domain
			const urlParts = uri.split("://");
			if (urlParts.length > 1) {
				path = urlParts[1].split("/").slice(1).join("/");
			}
		}

		const urlParts = path.split("/");
		const filename = urlParts[urlParts.length - 1];

		// Remove query parameters and hash if any
		const cleanFilename = filename.split("?")[0].split("#")[0];

		// Remove .pdf extension and decode if needed
		if (cleanFilename.toLowerCase().endsWith(".pdf")) {
			const nameWithoutExt = cleanFilename.slice(0, -4);
			// Try to decode URI component
			try {
				return decodeURIComponent(nameWithoutExt);
			} catch {
				return nameWithoutExt;
			}
		}

		// If no .pdf extension but URI suggests it's a PDF, try to use the filename anyway
		if (isPdfUri(uri) && cleanFilename) {
			try {
				return decodeURIComponent(cleanFilename);
			} catch {
				return cleanFilename;
			}
		}

		return null;
	} catch {
		return null;
	}
};

const styles = {
	left: StyleSheet.create({
		container: {
			flex: 1,
			alignItems: "flex-start"
		},
		wrapper: {
			borderRadius: 15,
			backgroundColor: "#f0f0f0",
			marginRight: 60,
			minHeight: 20,
			justifyContent: "flex-end"
		},
		containerToNext: {
			borderBottomLeftRadius: 3
		},
		containerToPrevious: {
			borderTopLeftRadius: 3
		},
		bottom: {
			flexDirection: "row",
			justifyContent: "flex-start"
		}
	}),
	right: StyleSheet.create({
		container: {
			flex: 1,
			alignItems: "flex-end"
		},
		wrapper: {
			borderRadius: 15,
			backgroundColor: COLOR2,
			marginLeft: 60,
			minHeight: 20,
			justifyContent: "flex-end"
		},
		containerToNext: {
			borderBottomRightRadius: 3
		},
		containerToPrevious: {
			borderTopRightRadius: 3
		},
		bottom: {
			flexDirection: "row",
			justifyContent: "flex-end"
		}
	}),
	content: StyleSheet.create({
		tick: {
			fontSize: 10,
			backgroundColor: "transparent",
			color: "#fff"
		},
		tickView: {
			flexDirection: "row",
			marginRight: 10
		},
		username: {
			top: -3,
			left: 0,
			fontSize: 12,
			backgroundColor: "transparent",
			color: "#aaa"
		},
		usernameView: {
			flexDirection: "row",
			marginHorizontal: 10
		}
	})
};

const Chat = () => {
	const dispatch = useDispatch();
	const navigation = useNavigation();
	const headerHeight = useHeaderHeight();
	const route = useRoute();

	const textInputRef = useRef();

	const [oldContent, setOldContent] = useState("");

	let {
		user,
		lang,
		templates,
		messages,
		isFetching,
		hasNextPage,
		nextPage,
		page,
		image,
		files,
		location,
		readyToCopyUrl,
		title,
		chatTo,
		templateId,
		youtubeVideoId,
		reload
	} = useSelector(getChatSelector, shallowEqual);

	let _id, pseudo, firstname, photoUri, gender, role;
	if (user) {
		({ _id, pseudo, firstname, photoUri, gender, role } = user);
	}

	if (messages) {
		messages = Object.values(messages);
	}

	const { to, eventId } = route.params;

	const {
		color2,
		bgColor1,
		bgColor2,
		bgColor5,
		lightgrey,
		bgWhite,
		white,
		darkgrey,
		flex1,
		justifyContentCenter,
		alignItemsCenter,
		w100p,
		cover,
		font,
		w150,
		h100,
		h100p,
		m10,
		rounded10,
		h44,
		overflowHidden
	} = commonStyles;

	useEffect(() => {
		if (eventId || to) {
			dispatch({
				type: SET_IN_CHAT_WITH,
				value: eventId ? eventId : to
			});
		}
		dispatch(fetchGetChatMessages(1, to, eventId, route.params.pseudo));
		return () => dispatch(fetchExitChat(to ? to : chatTo, eventId));
	}, []);

	// Another chat opened from this one (e.g. a private conversation from the
	// profile of a group chat member) replaces the shared chat state: load
	// this chat again when coming back to it
	const inChatWith = useSelector(state => state.chatReducer.inChatWith);
	const otherChatOpenedRef = useRef(false);
	const prevInChatWithRef = useRef(inChatWith);

	useEffect(() => {
		// The initial value may still be the chat this one was opened from
		if (prevInChatWithRef.current === inChatWith) {
			return;
		}
		prevInChatWithRef.current = inChatWith;
		const chatId = eventId || to || chatTo;
		if (inChatWith && chatId && inChatWith !== chatId) {
			otherChatOpenedRef.current = true;
		}
	}, [inChatWith]);

	useEffect(
		() =>
			navigation.addListener("focus", () => {
				if (otherChatOpenedRef.current) {
					otherChatOpenedRef.current = false;
					if (eventId || to) {
						dispatch({
							type: SET_IN_CHAT_WITH,
							value: eventId ? eventId : to
						});
					}
					dispatch(fetchGetChatMessages(1, to, eventId, route.params.pseudo));
				}
			}),
		[]
	);

	useEffect(() => {
		if (reload) {
			dispatch(fetchGetChatMessages(1, to, eventId, route.params.pseudo));
		}
	}, [reload]);

	useEffect(() => {
		if (templateId) {
			navigation.setOptions({
				title: getTranslatedProperty(templates[templateId], "name")
			});
		} else if (title) {
			navigation.setOptions({
				title
			});
		}
	}, [templateId, title]);

	useEffect(() => {
		if (chatTo) {
			dispatch({
				type: SET_IN_CHAT_WITH,
				value: chatTo
			});
		}
	}, [chatTo]);

	useEffect(() => {
		// messageContainerRef.current?.resetInputToolbar();
		textInputRef.current?.focus();
	}, [image, location, youtubeVideoId, files]);

	const sendMessage = message => {
		let computedLocation;
		if (location) {
			computedLocation = {
				latitude: location.coords.latitude,
				longitude: location.coords.longitude
			};
		}
		const msg = {
			to: to ? to : chatTo,
			eventId,
			text: message.text,
			image,
			files,
			location: computedLocation,
			youtubeVideoId
		};

		msg.meta = {
			user: {
				_id,
				name: pseudo.indexOf("@") < 0 ? pseudo : firstname,
				...(photoUri ? { avatar: photoUri } : {}),
				gender
			},
			websocket: true
		};
		msg.type = CHAT;

		dispatch(msg);
		setOldContent("");
	};

	const renderSend = ({ onSend, text, sendButtonProps, ...props }) => {
		const hasContent = Boolean(text || image || location || youtubeVideoId);

		return (
			<Send
				{...props}
				textStyle={hasContent ? color2 : lightgrey}
				disabled={!hasContent}
				label={t("chat:send")}
				sendButtonProps={{
					...sendButtonProps,
					onPress: () => onSend({ text: text.trim() }, true)
				}}
			/>
		);
	};

	const renderAvatar = props => {
		const { _id: userId, avatar, name, gender } = props.currentMessage?.user;

		return (
			<Avatar
				rounded
				size={40}
				source={getAvatarSource(avatar, gender)}
				title={name?.toUpperCase().substr(0, 2)}
				activeOpacity={0.7}
				// Shows the profile of the other users
				onPress={
					userId && userId !== user?._id
						? () => navigation.navigate("ProfileScreen", { userId })
						: undefined
				}
			/>
		);
	};

	const LocationView = ({ location, user }) => (
		<MapView
			style={[w150, h100, rounded10, m10]}
			initialRegion={{
				latitude: location.latitude,
				longitude: location.longitude,
				latitudeDelta: 0.0043,
				longitudeDelta: 0.0034
			}}
			disabled={true}
			onPress={() => {
				openURL(
					dispatch,
					`http://maps.google.com/?q=${location.latitude},${location.longitude}&title=${user?.name}`
				);
			}}
		>
			<MapViewMarker
				coordinate={{
					latitude: location.latitude,
					longitude: location.longitude
				}}
			/>
		</MapView>
	);

	const renderTicks = props => {
		const { currentMessage, renderTicks, user } = props;

		if (renderTicks && currentMessage) {
			return renderTicks(currentMessage);
		}
		if (
			currentMessage &&
			user &&
			currentMessage.user &&
			currentMessage.user._id !== user._id
		) {
			return null;
		}

		if (
			currentMessage &&
			(currentMessage.sent || currentMessage.received || currentMessage.pending)
		) {
			return (
				<View style={styles.content.tickView}>
					{!!currentMessage.sent && (
						<Text style={[styles.content.tick, props.tickStyle]}>✓</Text>
					)}
					{!!currentMessage.received && (
						<Text style={[styles.content.tick, props.tickStyle]}>✓</Text>
					)}
					{!!currentMessage.pending && (
						<Text style={[styles.content.tick, props.tickStyle]}>🕓</Text>
					)}
				</View>
			);
		}
		return null;
	};

	const renderTime = props => {
		if (props.currentMessage && props.currentMessage.createdAt) {
			const { containerStyle, wrapperStyle, textStyle, ...timeProps } = props;
			if (props.renderTime) {
				return props.renderTime(timeProps);
			}
			return <Time {...timeProps} />;
		}
		return null;
	};

	const renderUsername = props => {
		const { currentMessage, user, renderUsernameOnMessage, usernameStyle } =
			props;
		if (renderUsernameOnMessage && currentMessage) {
			if (user && currentMessage.user._id === user._id) {
				return null;
			}
			return (
				<View style={styles.content.usernameView}>
					<Text style={[styles.content.username, usernameStyle]}>
						~ {currentMessage.user.name}
					</Text>
				</View>
			);
		}
		return null;
	};

	const renderMessageText = props => {
		if (props.currentMessage && props.currentMessage.text) {
			const {
				containerStyle,
				wrapperStyle,
				optionTitles,
				...messageTextProps
			} = props;
			if (props.renderMessageText) {
				return props.renderMessageText(messageTextProps);
			}
			return <MessageText {...messageTextProps} />;
		}
		return null;
	};

	const styledBubbleToNext = props => {
		const { currentMessage, nextMessage, position, containerToNextStyle } =
			props;
		if (
			currentMessage &&
			nextMessage &&
			position &&
			isSameUser(currentMessage, nextMessage) &&
			isSameDay(currentMessage, nextMessage)
		) {
			return [
				styles[position].containerToNext,
				containerToNextStyle && containerToNextStyle[position]
			];
		}
		return null;
	};

	const styledBubbleToPrevious = props => {
		const {
			currentMessage,
			previousMessage,
			position,
			containerToPreviousStyle
		} = props;
		if (
			currentMessage &&
			previousMessage &&
			position &&
			isSameUser(currentMessage, previousMessage) &&
			isSameDay(currentMessage, previousMessage)
		) {
			return [
				styles[position].containerToPrevious,
				containerToPreviousStyle && containerToPreviousStyle[position]
			];
		}
		return null;
	};

	const renderBubble = props => {
		const {
			currentMessage,
			position,
			containerStyle,
			wrapperStyle,
			bottomContainerStyle
		} = props;

		if (currentMessage.location || currentMessage.youtubeVideoId) {
			return (
				<View
					style={[
						styles[position].container,
						containerStyle && containerStyle[position]
					]}
				>
					<View
						style={[
							styles[position].wrapper,
							styledBubbleToNext(props),
							styledBubbleToPrevious(props),
							wrapperStyle && wrapperStyle[position]
						]}
					>
						<TouchableOpacity
							onPress={() => {
								if (currentMessage.youtubeVideoId) {
									navigation.navigate("YoutubeScreen", {
										videoId: currentMessage.youtubeVideoId
									});
								}
							}}
							disabled={!!currentMessage.location}
						>
							{!!currentMessage.location ? (
								<LocationView
									location={currentMessage.location}
									user={currentMessage.user}
								/>
							) : (
								<YoutubeView youtubeVideoId={currentMessage.youtubeVideoId} />
							)}
							{renderMessageText(props)}
							<View
								style={[
									styles[position].bottom,
									bottomContainerStyle && bottomContainerStyle[position]
								]}
							>
								{renderUsername(props)}
								{renderTime(props)}
								{renderTicks(props)}
							</View>
						</TouchableOpacity>
					</View>
				</View>
			);
		}

		return (
			<Bubble
				{...props}
				wrapperStyle={{
					left: bgColor5,
					right: bgColor2
				}}
				textProps={{
					style: white
				}}
				textStyle={{
					left: [white, font],
					right: [white, font]
				}}
				timeTextStyle={{
					left: [white, font],
					right: [white, font]
				}}
				usernameStyle={white}
				renderTicks={() => renderTicks(props)}
			/>
		);
	};

	const renderDay = props => {
		return <Day {...props} textStyle={white} />;
	};

	const renderMessageImage = props => {
		const { currentMessage } = props;
		const imageUri = currentMessage.image;
		const messageFiles = currentMessage.files || [];
		const isPdf =
			isPdfUri(imageUri) ||
			messageFiles.some(
				f =>
					f.base64?.startsWith("data:application/pdf") ||
					f.name?.toLowerCase().endsWith(".pdf")
			);

		if (isPdf) {
			// Extract PDF name from multiple sources
			const pdfFile = messageFiles.find(
				f =>
					f.base64?.startsWith("data:application/pdf") ||
					f.name?.toLowerCase().endsWith(".pdf")
			);

			// Try to get name from: files array, URI (filename), text field, or default
			// Priority: filename from files > filename from URI > message text > default
			const pdfName =
				pdfFile?.name ||
				extractPdfNameFromUri(imageUri) ||
				currentMessage.text ||
				t("chat:pdfdocument");

			return (
				<TouchableOpacity
					style={[
						{
							height: 120,
							width: 150,
							backgroundColor: COLOR5,
							borderRadius: 10,
							justifyContent: "center",
							alignItems: "center",
							margin: 10,
							padding: 10
						}
					]}
					onPress={() => {
						navigation.navigate("DocReaderScreen", {
							uri: imageUri,
							sharing: true
						});
					}}
				>
					<MaterialCommunityIcons
						name="file-pdf-box"
						size={50}
						color={COLOR2}
					/>
					<Text
						style={[
							font,
							{
								color: COLOR2,
								fontSize: 12,
								marginTop: 8,
								textAlign: "center"
							}
						]}
						numberOfLines={2}
					>
						{pdfName}
					</Text>
				</TouchableOpacity>
			);
		}

		return (
			<Avatar
				containerStyle={[w150, h100, rounded10, m10]}
				renderPlaceholderContent={
					<Image
						style={[w150, h100, rounded10]}
						source={getImageUri("placeholder")}
					/>
				}
				source={{
					uri: imageUri
				}}
				overlayContainerStyle={[rounded10]}
				onPress={() => {
					navigation.navigate("ImageZoomScreen", {
						uri: imageUri
					});
				}}
			/>
		);
	};

	const renderLoadEarlier = props => {
		return (
			hasNextPage && <LoadEarlier {...props} label={t("chat:loadearlier")} />
		);
	};

	const _pickImage = async () => {
		const image = await pickImage(dispatch);

		if (image) {
			dispatch({
				type: SET_CHAT_PHOTO_DATA,
				...image
			});
		}
	};

	const _pickDocument = async () => {
		const document = await pickDocument(dispatch);

		if (document) {
			dispatch({
				type: SET_CHAT_PHOTO_DATA,
				...document
			});
		}
	};

	const handleOnChangeText = async content => {
		const regEnd = /^[-_a-zA-Z0-9][\n.!, ]$/;
		if (
			content === "" ||
			image ||
			location ||
			youtubeVideoId ||
			oldContent.length > content.length ||
			content.length < 8 ||
			!regEnd.test(content.substring(content.length - 2))
		) {
			setOldContent(content);
			return;
		}
		setOldContent(content);
		let match;
		if (
			(match = content.match(
				/^.*((http:\/\/|https:\/\/|www.)\S+?\.(?:jpe?g|gif|png|tiff|svg))[\n.!, ]$/
			))
		) {
			dispatch({
				type: SET_CHAT_PHOTO_DATA,
				value: match[1]
			});
		}
		if (
			(match = content.match(
				/^.*(https:\/\/www.|https:\/\/)youtube.com\/watch\?v=([a-zA-Z0-9-_]+)(\&[a-zA-Z0-9=-_]+)?[\n.!, ]$/
			)) ||
			(match = content.match(
				/^.*(https:\/\/www.|https:\/\/)youtu.be\/([-_a-zA-Z0-9]+)(\?\S+=\S+)?[\n.!, ]$/
			)) ||
			// Youtube live
			(match = content.match(
				/^.*(https:\/\/www.|https:\/\/)www.youtube.com\/live\/([-_a-zA-Z0-9]+)(\?\S+=\S+)?[\n.!, ]$/
			))
		) {
			dispatch({
				type: SET_CHAT_VIDEO_ID,
				value: match[2]
			});
			dispatch({
				type: SET_CHAT_THUMBNAIL_URL,
				value: (await getYoutubeMeta(match[2])).thumbnail_url
			});
		}
	};

	const renderInputToolbar = props =>
		youtubeVideoId || location || image || !isAndroid ? (
			<View style={isAndroid ? [{ minHeight: 170 }, bgWhite] : [bgWhite]}>
				{youtubeVideoId && image && (
					<View style={{ minHeight: 60 }}>
						<Tile
							activeOpacity={0.5}
							containerStyle={[w150, h100, rounded10, m10, overflowHidden]}
							imageSrc={{ uri: image }}
							imageContainerStyle={[rounded10, w150, h100]}
							icon={{
								name: "play-circle",
								type: "font-awesome",
								size: 30,
								color: COLOR2
							}}
							onPress={() => {
								dispatch({ type: CLEAR_CHAT_VIDEO_ID });
							}}
							featured
						/>
						<TouchableOpacity
							style={{ position: "absolute", top: 10, right: 10 }}
							onPress={() => {
								dispatch({ type: CLEAR_CHAT_VIDEO_ID });
							}}
						>
							<MaterialCommunityIcons
								name="delete-forever"
								color="red"
								size={20}
							/>
						</TouchableOpacity>
					</View>
				)}
				{location && (
					<View style={{ minHeight: 60 }}>
						<MapView
							style={[w150, h100, rounded10, m10]}
							initialRegion={{
								latitude: location.coords.latitude,
								longitude: location.coords.longitude,
								latitudeDelta: 0.0043,
								longitudeDelta: 0.0034
							}}
							scrollEnabled={false}
							zoomEnabled={false}
						>
							<MapViewMarker
								coordinate={{
									latitude: location.coords.latitude,
									longitude: location.coords.longitude
								}}
							/>
							<MaterialCommunityIcons
								style={{ position: "absolute", top: 10, right: 10 }}
								name="delete-forever"
								color="red"
								size={20}
								onPress={() =>
									dispatch({
										type: CLEAR_CHAT_MAP_DATA
									})
								}
							/>
						</MapView>
					</View>
				)}
				{image && !youtubeVideoId && (
					<View style={{ minHeight: 60 }}>
						{isPdfUri(image) ||
						files?.some(
							f =>
								f.base64?.startsWith("data:application/pdf") ||
								f.name?.toLowerCase().endsWith(".pdf")
						) ? (
							<View style={{ position: "relative" }}>
								<TouchableOpacity
									style={[
										{
											height: 120,
											width: 150,
											backgroundColor: COLOR5,
											borderRadius: 10,
											margin: 10,
											justifyContent: "center",
											alignItems: "center",
											padding: 10
										}
									]}
									onPress={() => {
										navigation.navigate("DocReaderScreen", {
											uri: image,
											sharing: true
										});
									}}
								>
									<MaterialCommunityIcons
										name="file-pdf-box"
										size={50}
										color={COLOR2}
									/>
									<Text
										style={[
											font,
											{
												color: COLOR2,
												fontSize: 12,
												marginTop: 8,
												textAlign: "center"
											}
										]}
										numberOfLines={2}
									>
										{files?.find(
											f =>
												f.base64?.startsWith("data:application/pdf") ||
												f.name?.toLowerCase().endsWith(".pdf")
										)?.name || "Document PDF"}
									</Text>
								</TouchableOpacity>
								<TouchableOpacity
									style={{ position: "absolute", top: 10, right: 10 }}
									onPress={() => {
										dispatch({
											type: CLEAR_CHAT_PHOTO_DATA
										});
									}}
								>
									<MaterialCommunityIcons
										name="delete-forever"
										color="red"
										size={20}
									/>
								</TouchableOpacity>
							</View>
						) : (
							<Avatar
								containerStyle={[w150, h100, rounded10, m10]}
								// placeholderStyle={[rounded10]}
								renderPlaceholderContent={
									<Image
										style={[w150, h100, rounded10]}
										source={getImageUri("placeholder")}
									/>
								}
								source={{
									uri: image
								}}
								overlayContainerStyle={[rounded10]}
								onPress={() => {
									dispatch({
										type: CLEAR_CHAT_PHOTO_DATA
									});
								}}
							>
								<MaterialCommunityIcons
									style={{ position: "absolute", top: 10, right: 10 }}
									name="delete-forever"
									color="red"
									size={20}
								/>
							</Avatar>
						)}
					</View>
				)}
				<InputToolbar {...props} />
			</View>
		) : (
			<InputToolbar {...props} />
		);

	const renderComposer = props => {
		return (
			<Composer
				{...props}
				textInputProps={{
					...props.textInputProps
					// value: oldContent
				}}
				onTextChanged={text => {
					props.onTextChanged(text);
					handleOnChangeText(text);
				}}
			/>
		);
	};

	const _currentLocation = async () => {
		let { status } = await Location.requestForegroundPermissionsAsync();
		if (status !== "granted") {
			dispatch({
				type: SET_DROP_DOWN_ALERT_INFO,
				info: "locationinfo"
			});
			return;
		}

		const location = await Location.getCurrentPositionAsync({
			accuracy: isiOS ? Location.Accuracy.Lowest : Location.Accuracy.Low
		});
		dispatch({
			type: SET_CHAT_CURRENT_LOCATION,
			location
		});
	};

	const renderActions = props =>
		role !== ROLE_NEIGHBOR && (
			<Actions
				{...props}
				containerStyle={[
					h44,
					alignItemsCenter,
					justifyContentCenter,
					{ marginHorizontal: 4, marginBottom: 0 }
				]}
				icon={() => (
					<Image style={{ width: 32, height: 32 }} source={getImage("plus")} />
				)}
				options={{
					[t("button:copygiphy")]: () => {
						dispatch({ type: SET_READY_TO_COPY_URL });
						openBrowser(dispatch, "https://giphy.com/search/neighborhood");
					},
					[t("button:copyyoutube")]: () => {
						dispatch({ type: SET_READY_TO_COPY_URL });
						openBrowser(dispatch, "https://www.youtube.com");
					},
					[t("button:shareposition")]: _currentLocation,
					[t("button:camera")]: () => {
						navigation.navigate("CameraScreen", {
							from: "chat"
						});
					},
					[t("button:albums")]: _pickImage,
					[t("button:pdfdocument")]: _pickDocument,
					[t("button:cancel")]: () => {}
				}}
				optionTintColor={COLOR2}
			/>
		);

	return (
		<ImageBackground
			source={getImageUri("chat")}
			style={[
				w100p,
				{
					height: SCREEN_HEIGHT - headerHeight,
					backgroundColor: "rgba(255,255,255,0.5)"
				},
				cover
			]}
		>
			<SafeAreaView
				style={[
					flex1,
					{
						backgroundColor: "rgba(255,255,255,0.5)"
					}
				]}
			>
				{isFetching && page === 1 ? (
					<ActivityIndicator
						style={[flex1, justifyContentCenter]}
						size="large"
						color={COLOR2}
					/>
				) : (
					<GiftedChat
						textInputRef={textInputRef}
						disabled={!to && !pseudo && !eventId}
						messageIdGenerator={isWeb ? v4 : undefined}
						messages={messages}
						onSend={messages => sendMessage(messages[0])}
						user={{
							_id,
							name: pseudo,
							avatar: photoUri,
							gender
						}}
						placeholder={t("chat:placeholder")}
						bottomOffset={0}
						showUserAvatar={true}
						renderUsernameOnMessage={Boolean(eventId)}
						showAvatarForEveryMessage={true}
						renderAvatar={renderAvatar}
						renderBubble={renderBubble}
						renderDay={renderDay}
						renderMessageImage={renderMessageImage}
						renderComposer={renderComposer}
						locale={lang}
						infiniteScroll={true}
						text={oldContent}
						onLoadEarlier={() =>
							hasNextPage &&
							dispatch(
								fetchGetChatMessages(nextPage, to, eventId, route.params.pseudo)
							)
						}
						isLoadingEarlier={isFetching}
						loadEarlier={true}
						renderLoadEarlier={renderLoadEarlier}
						renderInputToolbar={renderInputToolbar}
						renderSend={renderSend}
						minInputToolbarHeight={
							Boolean(image || location || youtubeVideoId) ? 170 : 44
						}
						alwaysShowSend={true}
						renderActions={renderActions}
						textInputProps={{
							onFocus: async () => {
								if (readyToCopyUrl) {
									let text = null;
									try {
										text = await Clipboard.getStringAsync();
										const re =
											/^((http:\/\/|https:\/\/|www.)\S+?\.(?:jpe?g|gif|png|tiff|svg))$/;
										if (re.test(text)) {
											dispatch({
												type: SET_CHAT_PHOTO_DATA,
												value: text
											});
										}
										let match;
										if (
											(match = text.match(
												/^(https:\/\/www.|https:\/\/)youtube.com\/watch\?v=([a-zA-Z0-9-_]+)(\&[a-zA-Z0-9=-_]+)?$/
											)) ||
											(match = text.match(
												/^(https:\/\/www.|https:\/\/)youtu.be\/([-_a-zA-Z0-9]+)(\?\S+=\S+)?$/
											)) ||
											// Youtube live
											(match = text.match(
												/^(https:\/\/www.|https:\/\/)www.youtube.com\/live\/([-_a-zA-Z0-9]+)(\?\S+=\S+)?$/
											))
										) {
											dispatch({
												type: SET_CHAT_VIDEO_ID,
												value: match[2]
											});
											dispatch({
												type: SET_CHAT_THUMBNAIL_URL,
												value: (await getYoutubeMeta(match[2])).thumbnail_url
											});
										}
									} catch (error) {
										console.error(error);
									} finally {
										dispatch({ type: CLEAR_READY_TO_COPY_URL, text });
									}
								}
							}
						}}
					/>
				)}
			</SafeAreaView>
		</ImageBackground>
	);
};

const MemoizedChat = memo(Chat);
export default MemoizedChat;
